"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";

import { Link } from "@/components/link/link.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { appendQueryString } from "@/utils/url.util";

import {
  DEFAULT_BASE_SYSTEM_DRAWER,
  DEFAULT_PRIMARY_BUTTON_LABEL,
  DEFAULT_SECONDARY_BUTTON_LABEL,
} from "@/app/blu/monitoring/components/monitoringPlansSection/monitoringPlansSection.defaults";
import { ConfiguratorHeaderProduct } from "@/app/blu/shop/build-my-system/components/configuratorBasePackage/configuratorBasePackageProduct.component";
import type { GetMonitoringPageBaseSystemDrawerQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { BenefitsRichTextList } from "./benefitsRichTextList.component";

export const MonitoringPlanSystemDrawerContent = ({
  baseSystem,
}: MonitoringPlanSystemDrawerContentProps) => {
  const pathname = usePathname();

  // const title = baseSystem?.drawerTitle;
  const includesHeading =
    baseSystem?.includesHeading ||
    DEFAULT_BASE_SYSTEM_DRAWER.DEFAULT_INCLUDES_HEADING;
  const benefitsHeading = baseSystem?.benefitsHeading;
  const benefitsImageUrl = baseSystem?.benefitsImage
    ? urlFor(baseSystem.benefitsImage).width(1200).url()
    : null;
  const showBenefitsSection =
    (!!baseSystem && baseSystem.benefits) || !!benefitsImageUrl;

  const intro = baseSystem?.intro;

  const renderIncludedProducts = (items?: IncludedItem[] | null) => {
    if (!items?.length) return null;

    return items.map((included, index) => {
      const equipmentProductId = included.product?.id;
      return (
        <ConfiguratorHeaderProduct
          key={included._key}
          showViewDetailsLink={!!included.showViewDetailsLink}
          productId={equipmentProductId}
          title={included.title}
          subtitle={included.subtitle}
          image={included.image}
          imageAlt={included.alt}
          minimal={index >= 2}
        />
      );
    });
  };

  const renderFooterButtons = () => {
    const primaryLabel =
      baseSystem?.primaryFooterButton?.label || DEFAULT_PRIMARY_BUTTON_LABEL;
    const secondaryLabel =
      baseSystem?.secondaryFooterButton?.label ||
      DEFAULT_SECONDARY_BUTTON_LABEL;

    const primaryLink = baseSystem?.primaryFooterButton?.link;
    const secondaryLink = baseSystem?.secondaryFooterButton?.link;
    const primaryHref = primaryLink?.url
      ? (appendQueryString(primaryLink.url, primaryLink.queryString) ??
        primaryLink.url)
      : null;
    const secondaryHref = secondaryLink?.url
      ? (appendQueryString(secondaryLink.url, secondaryLink.queryString) ??
        secondaryLink.url)
      : null;

    return (
      <>
        {/* Primary button */}
        <Link
          secondary
          href={primaryHref ?? pathname}
          replace
          data-testid="build-my-system-button"
          className="px-6 text-center"
          target={primaryLink?.openInNewTab ? "_blank" : undefined}
          rel={primaryLink?.openInNewTab ? "noopener noreferrer" : undefined}>
          {primaryLabel}
        </Link>

        {/* Secondary button */}
        <Link
          secondary
          border
          href={secondaryHref ?? pathname}
          className="px-6 text-center"
          replace
          data-testid="shop-kits-button"
          target={secondaryLink?.openInNewTab ? "_blank" : undefined}
          rel={secondaryLink?.openInNewTab ? "noopener noreferrer" : undefined}>
          {secondaryLabel}
        </Link>
      </>
    );
  };

  return (
    <div className="flex h-full max-h-full min-h-0 flex-col overflow-hidden">
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="flex flex-col gap-6 pt-4 pb-6">
          {intro ? (
            <div className="text-greyscale-body [&_p]:text-greyscale-body mx-6">
              <RichText>{intro}</RichText>
            </div>
          ) : (
            <Text
              variant="body1"
              component="p"
              className="text-greyscale-body mx-6">
              {DEFAULT_BASE_SYSTEM_DRAWER.DEFAULT_INTRO_FALLBACK}
            </Text>
          )}

          <div className="bg-greyscale-background/80 mx-4 rounded-2xl px-4 py-6 sm:px-6">
            <Text
              variant="h6"
              component="p"
              className="text-greyscale-headline mb-6 text-center !text-[24px]">
              {includesHeading}
            </Text>

            <ul
              className="flex flex-col gap-4"
              data-testid="monitoring-system-drawer-included-products-list">
              {renderIncludedProducts(baseSystem?.whatsIncluded)}
            </ul>
          </div>

          {showBenefitsSection ? (
            <div className="flex flex-col gap-4 px-1 sm:px-0">
              {benefitsImageUrl ? (
                <div className="bg-greyscale-background relative aspect-video w-full overflow-hidden">
                  <Image
                    src={benefitsImageUrl}
                    alt=""
                    fill
                    className="object-cover"
                    sizes="(max-width: 900px) 100vw, 900px"
                  />
                </div>
              ) : null}
              <div className="mx-6 flex flex-col gap-2">
                <Text
                  variant="body1"
                  component="h3"
                  className="text-greyscale-headline font-bold">
                  {benefitsHeading}
                </Text>
                {baseSystem && baseSystem.benefits ? (
                  <BenefitsRichTextList benefits={baseSystem.benefits} />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <footer className="border-greyscale-strength/20 flex shrink-0 flex-row justify-center gap-3 border-t bg-white px-2 py-4 shadow-[0_-12px_24px_0_rgba(0,0,0,0.14)] sm:justify-end sm:gap-4 sm:px-8">
        {renderFooterButtons()}
      </footer>
    </div>
  );
};

export type MonitoringPlanSystemDrawerContentProps = {
  baseSystem: MonitoringPlanSystemDrawerBaseSystem | null;
};

type IncludedItem = NonNullable<
  MonitoringPlanSystemDrawerBaseSystem["whatsIncluded"]
>[number];

/** Drawer CMS shape from `getMonitoringPageBaseSystemDrawerQuery`. */
export type MonitoringPlanSystemDrawerBaseSystem = NonNullable<
  NonNullable<GetMonitoringPageBaseSystemDrawerQueryResult>["baseSystemDrawer"]
>;
